import React, { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CreditCard, Landmark, Plus, Pencil, Trash2, AlertTriangle, TrendingDown, TrendingUp, Link2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "../components/ui/checkbox";

const defaultForm = {
  name: "",
  type: "credit_card",
  balance: "",
  apr: "",
  minimum_payment: "",
  credit_limit: "",
  due_day_of_month: "",
};

const dtiChipClass = (status) => ({
  excellent: "bg-emerald-500/95 text-white",
  healthy: "bg-emerald-500/80 text-white",
  caution: "bg-amber-500/95 text-white",
  high_risk: "bg-rose-600 text-white",
  unknown: "bg-white/15 text-white/80",
}[status] || "bg-white/15 text-white/80");

const dtiLabel = (status) => ({
  excellent: "Excellent",
  healthy: "Healthy",
  caution: "Caution",
  high_risk: "High risk",
  unknown: "—",
}[status] || "—");

export default function Debts() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [data, setData] = useState({ debts: [], totals: null });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [suggestions, setSuggestions] = useState({}); // { [debtId]: { count, total, transactions } }
  const [syncDebt, setSyncDebt] = useState(null); // debt object when sync dialog is open
  const [syncSelected, setSyncSelected] = useState({});
  const [syncSaving, setSyncSaving] = useState(false);
  const [detailDebt, setDetailDebt] = useState(null); // opens the detail dialog
  const [payments, setPayments] = useState([]);
  const [payAmount, setPayAmount] = useState("");
  const [payDesc, setPayDesc] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [paySaving, setPaySaving] = useState(false);
  // Per-debt "auto-sync dismissed" set — persisted on the backend so it syncs
  // across devices. localStorage is used as a fallback + one-time migration.
  const LEGACY_DISMISS_KEY = "argent-debt-sync-dismissed";
  const [dismissedSync, setDismissedSync] = useState(new Set());
  const SORT_KEY = "argent-debts-sort";
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(SORT_KEY) || "default");
  const setAndSaveSort = (v) => { setSortBy(v); try { localStorage.setItem(SORT_KEY, v); } catch {} };

  const loadDismissed = async () => {
    try {
      const r = await api.get("/preferences/sync-dismissed");
      const backendIds = new Set(r.data?.debt_ids || []);
      // Migrate any legacy localStorage entries once
      try {
        const raw = localStorage.getItem(LEGACY_DISMISS_KEY);
        if (raw) {
          const legacyIds = JSON.parse(raw) || [];
          const toMigrate = legacyIds.filter(id => !backendIds.has(id));
          for (const id of toMigrate) {
            try {
              await api.post("/preferences/sync-dismissed", { debt_id: id });
              backendIds.add(id);
            } catch {}
          }
          localStorage.removeItem(LEGACY_DISMISS_KEY);
        }
      } catch {}
      setDismissedSync(backendIds);
    } catch {
      // Fallback to localStorage if backend fails
      try {
        const raw = localStorage.getItem(LEGACY_DISMISS_KEY);
        setDismissedSync(new Set(raw ? JSON.parse(raw) : []));
      } catch { setDismissedSync(new Set()); }
    }
  };

  const dismissSyncFor = async (debtId) => {
    // Optimistic update
    setDismissedSync(prev => { const next = new Set(prev); next.add(debtId); return next; });
    try {
      await api.post("/preferences/sync-dismissed", { debt_id: debtId });
    } catch {
      // Roll back on failure
      setDismissedSync(prev => { const next = new Set(prev); next.delete(debtId); return next; });
      toast.error("Couldn't hide — try again");
      return;
    }
    toast.success("Auto-sync hidden for this debt", {
      duration: 6000,
      action: {
        label: "Show again",
        onClick: async () => {
          setDismissedSync(prev => { const next = new Set(prev); next.delete(debtId); return next; });
          try { await api.delete(`/preferences/sync-dismissed/${debtId}`); } catch {}
        },
      },
    });
  };

  const load = async () => {
    const r = await api.get("/debts/projection");
    setData(r.data);
    // Prefetch suggestions in parallel for every debt
    const map = {};
    await Promise.all(
      (r.data.debts || []).map(async (d) => {
        try {
          const s = await api.get(`/debts/${d.id}/suggested-payments`);
          if ((s.data?.count || 0) > 0) map[d.id] = s.data;
        } catch { /* ignore per-debt errors */ }
      })
    );
    setSuggestions(map);
  };
  useEffect(() => { load(); loadDismissed(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(defaultForm);
    setOpen(true);
  };
  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name,
      type: d.type,
      balance: String(d.balance),
      apr: String(d.apr),
      minimum_payment: String(d.minimum_payment),
      credit_limit: d.credit_limit != null ? String(d.credit_limit) : "",
      due_day_of_month: d.due_day_of_month != null ? String(d.due_day_of_month) : "",
    });
    setOpen(true);
  };
  const save = async () => {
    const body = {
      name: form.name.trim(),
      type: form.type,
      balance: parseFloat(form.balance || 0),
      apr: parseFloat(form.apr || 0),
      minimum_payment: parseFloat(form.minimum_payment || 0),
      credit_limit: form.type === "credit_card" && form.credit_limit
        ? parseFloat(form.credit_limit)
        : null,
      due_day_of_month: form.due_day_of_month
        ? parseInt(form.due_day_of_month, 10)
        : null,
    };
    if (!body.name) { toast.error("Name required"); return; }
    try {
      if (editingId) await api.put(`/debts/${editingId}`, body);
      else await api.post("/debts", body);
      setOpen(false);
      load();
      toast.success(editingId ? "Debt updated" : "Debt added");
    } catch (e) {
      toast.error("Couldn't save");
    }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    await api.delete(`/debts/${d.id}`);
    load();
    toast.success("Removed");
  };

  const openDetail = async (d) => {
    setDetailDebt(d);
    setPayAmount(String(d.minimum_payment || ""));
    setPayDesc("");
    setPayDate(new Date().toISOString().slice(0, 10));
    try {
      const r = await api.get(`/debts/${d.id}/payments`);
      setPayments(r.data || []);
    } catch { setPayments([]); }
  };
  const closeDetail = () => {
    setDetailDebt(null);
    setPayments([]);
  };
  const logPayment = async () => {
    if (!detailDebt) return;
    const amt = parseFloat(payAmount || 0);
    if (amt <= 0) { toast.error("Amount must be greater than zero"); return; }
    setPaySaving(true);
    try {
      const r = await api.post(`/debts/${detailDebt.id}/log-payment`, {
        amount: amt,
        description: payDesc || `Payment to ${detailDebt.name}`,
        date: new Date(payDate).toISOString(),
      });
      toast.success(`Logged ${fmtMoney(r.data.reduced_by, cur)} · new balance ${fmtMoney(r.data.new_balance, cur)}`);
      await load();
      const fresh = await api.get(`/debts/${detailDebt.id}/payments`);
      setPayments(fresh.data || []);
      setPayAmount("");
      setPayDesc("");
    } catch {
      toast.error("Couldn't log payment");
    } finally {
      setPaySaving(false);
    }
  };

  const undoPayment = async (p) => {
    if (!detailDebt) return;
    if (!window.confirm(`Undo this ${fmtMoney(p.amount, cur)} payment? The transaction will be deleted and the balance restored.`)) return;
    try {
      const r = await api.delete(`/debts/${detailDebt.id}/payments/${p.id}`);
      toast.success(`Restored ${fmtMoney(r.data.restored_by, cur)} · balance now ${fmtMoney(r.data.new_balance, cur)}`);
      await load();
      const fresh = await api.get(`/debts/${detailDebt.id}/payments`);
      setPayments(fresh.data || []);
    } catch {
      toast.error("Couldn't undo payment");
    }
  };

  // Keep detailDebt in sync with data.debts (so the detail dialog reflects the
  // latest balance / projections after a payment or auto-sync)
  useEffect(() => {
    if (!detailDebt) return;
    const fresh = (data.debts || []).find(x => x.id === detailDebt.id);
    if (fresh) setDetailDebt(fresh);
  }, [data]);

  const openSync = (d) => {
    setSyncDebt(d);
    const s = suggestions[d.id];
    const initial = {};
    (s?.transactions || []).forEach(t => { initial[t.id] = t.confidence === "high"; });
    setSyncSelected(initial);
  };
  const applySync = async () => {
    if (!syncDebt) return;
    const ids = Object.keys(syncSelected).filter(k => syncSelected[k]);
    if (ids.length === 0) { toast.error("Nothing selected"); return; }
    setSyncSaving(true);
    try {
      const r = await api.post(`/debts/${syncDebt.id}/apply-payments`, { transaction_ids: ids });
      toast.success(`Applied ${r.data.applied} — balance reduced by ${fmtMoney(r.data.reduced_by, cur)}`);
      setSyncDebt(null);
      load();
    } catch {
      toast.error("Couldn't apply payments");
    } finally {
      setSyncSaving(false);
    }
  };

  const totals = data.totals || {};
  const debts = data.debts || [];
  const has = debts.length > 0;
  const proj = totals.projections || {};
  const totalDelta12 = has ? (proj["12"]?.final_balance || 0) - (totals.current_balance || 0) : 0;

  // Sort debts for the card grid only. "default" is soonest-due first
  // (uses `next_due.days_until` from /api/debts/projection; debts with no
  // due date sink to the bottom); "balance_desc" is greatest→least; and
  // "name_asc" is alphabetical A→Z.
  const sortedDebts = React.useMemo(() => {
    const arr = [...debts];
    if (sortBy === "balance_desc") {
      arr.sort((a, b) => (b.balance || 0) - (a.balance || 0));
    } else if (sortBy === "name_asc") {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    } else {
      // default: soonest due-date first; nulls last; tiebreak by higher balance
      arr.sort((a, b) => {
        const da = a?.next_due?.days_until;
        const db = b?.next_due?.days_until;
        const aHas = typeof da === "number";
        const bHas = typeof db === "number";
        if (aHas && bHas && da !== db) return da - db;
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;
        return (b.balance || 0) - (a.balance || 0);
      });
    }
    return arr;
  }, [debts, sortBy]);

  return (
    <Shell>
      <div className="mb-4 sm:mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">Credit & Loans</h1>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
            Track balances, APR, and projected payoff for every card and loan
          </p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="add-debt-btn">
          <Plus className="w-4 h-4 mr-1" /> Add debt
        </Button>
      </div>

      {/* Summary + projection */}
      {has && (
        <div className="grid grid-cols-12 gap-4 sm:gap-6 mb-6">
          <div
            data-testid="debt-summary-card"
            className="col-span-12 lg:col-span-7 rounded-3xl p-5 sm:p-8 relative overflow-hidden border border-slate-200 dark:border-transparent bg-white text-slate-900 suit-hero-dark dark:text-white dark:web-pattern"
          >
            <div className="text-[10px] sm:text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-white/70">
              Total {totals.count === 1 ? "balance" : `across ${totals.count} accounts`}
            </div>
            <div className="font-display text-4xl sm:text-5xl font-bold mt-2 tabular" data-testid="debt-total-balance">
              {fmtMoney(totals.current_balance || 0, cur)}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-6">
              <div>
                <div className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-white/60">Blended APR</div>
                <div className="text-lg sm:text-xl font-bold tabular">{totals.blended_apr}%</div>
              </div>
              <div>
                <div className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-white/60">Paying / mo</div>
                <div className="text-lg sm:text-xl font-bold tabular text-emerald-600 dark:text-emerald-300">{fmtMoney(totals.monthly_payment || 0, cur)}</div>
              </div>
              <div>
                <div className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-white/60">In 6 months</div>
                <div className="text-lg sm:text-xl font-bold tabular">{fmtMoney(proj["6"]?.final_balance || 0, cur)}</div>
              </div>
              <div>
                <div className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-white/60">In 12 months</div>
                <div className="text-lg sm:text-xl font-bold tabular">{fmtMoney(proj["12"]?.final_balance || 0, cur)}</div>
              </div>
            </div>

            {totals.dti_ratio != null && (
              <div
                data-testid="dti-chip"
                className={`mt-5 inline-flex items-center gap-2.5 rounded-full px-3.5 py-2 ${dtiChipClass(totals.dti_status)}`}
                title="Debt-to-income = your total monthly debt payments divided by average monthly income over the last 3 months. Lenders like this under 36%."
              >
                <span className="text-[10px] font-bold tracking-widest uppercase opacity-80">DTI</span>
                <span className="tabular text-lg font-bold">{totals.dti_ratio}%</span>
                <span className="text-[10px] font-bold uppercase tracking-widest">{dtiLabel(totals.dti_status)}</span>
                <span className="text-[10px] opacity-80">
                  · <span className="tabular">{fmtMoney(totals.monthly_payment || 0, cur)}</span> of <span className="tabular">{fmtMoney(totals.monthly_income || 0, cur)}</span>/mo
                </span>
              </div>
            )}
            {totals.dti_ratio == null && totals.count > 0 && (
              <div data-testid="dti-chip" className="mt-5 inline-flex items-center gap-2 rounded-full px-3.5 py-2 bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/70 text-xs">
                <span className="font-bold uppercase tracking-widest text-[10px]">DTI</span>
                <span>Add an income transaction in the last 3 months to see your ratio</span>
              </div>
            )}
            <div className="mt-5 flex items-center gap-2 text-xs sm:text-sm">
              {totalDelta12 < 0 ? (
                <>
                  <TrendingDown className="w-4 h-4 text-emerald-600 dark:text-emerald-300" />
                  <span className="font-semibold">
                    On track — down <span className="tabular">{fmtMoney(Math.abs(totalDelta12), cur)}</span> in the next 12 months
                  </span>
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4 text-rose-600 dark:text-rose-300" />
                  <span className="font-semibold">
                    Warning — growing by <span className="tabular">{fmtMoney(totalDelta12, cur)}</span> in the next 12 months
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 grid grid-cols-3 gap-3">
            {[
              { l: "3 months", v: proj["3"], accent: "text-slate-900 dark:text-slate-100" },
              { l: "6 months", v: proj["6"], accent: "text-slate-900 dark:text-slate-100" },
              { l: "12 months", v: proj["12"], accent: "text-slate-900 dark:text-slate-100" },
            ].map((h) => (
              <div key={h.l} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-4 sm:p-5">
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-600 dark:text-slate-300">{h.l}</div>
                <div className={`font-display text-lg sm:text-2xl font-bold tabular mt-2 break-words ${h.accent}`}>
                  {fmtMoney(h.v?.final_balance || 0, cur)}
                </div>
                <div className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-300 mt-1">
                  Interest paid <span className="tabular text-rose-600 dark:text-rose-400 font-semibold">{fmtMoney(h.v?.total_interest || 0, cur)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!has && (
        <div className="rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800 p-10 text-center" data-testid="debt-empty-state">
          <CreditCard className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="mt-3 font-display text-lg font-semibold text-slate-800 dark:text-slate-100">No credit cards or loans yet</div>
          <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">Add one to see your projected payoff.</div>
          <Button onClick={openNew} className="mt-5 rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="empty-add-debt-btn">
            <Plus className="w-4 h-4 mr-1" /> Add your first debt
          </Button>
        </div>
      )}

      {/* Per-debt cards */}
      {has && (
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
            {sortedDebts.length} account{sortedDebts.length === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Sort by</span>
            <Select value={sortBy} onValueChange={setAndSaveSort}>
              <SelectTrigger data-testid="debts-sort-select" className="rounded-full w-[210px] h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default" data-testid="debts-sort-default">Next due (default)</SelectItem>
                <SelectItem value="balance_desc" data-testid="debts-sort-balance">Balance · high to low</SelectItem>
                <SelectItem value="name_asc" data-testid="debts-sort-name">Name · A to Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {sortedDebts.map((d) => (
          <DebtCard
            key={d.id}
            d={d}
            cur={cur}
            suggestion={dismissedSync.has(d.id) ? null : suggestions[d.id]}
            onEdit={openEdit}
            onDelete={del}
            onSync={openSync}
            onOpen={openDetail}
            onDismissSync={() => dismissSyncFor(d.id)}
          />
        ))}
      </div>

      {/* Debt Detail dialog: projections + payment history + log-a-payment */}
      <Dialog open={!!detailDebt} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] flex flex-col p-0" data-testid="debt-detail-dialog">
          <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6">
            <DialogTitle className="font-display text-xl sm:text-2xl flex items-center gap-3">
              {detailDebt?.type === "credit_card"
                ? <CreditCard className="w-5 h-5 spidey-red" />
                : <Landmark className="w-5 h-5 text-stone-700 dark:text-stone-400" />}
              {detailDebt?.name}
            </DialogTitle>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
              {detailDebt?.type === "credit_card" ? "Credit card" : "Loan"} · {detailDebt?.apr}% APR
            </p>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 px-5 sm:px-6 py-4 space-y-5">
            {/* Balance + projections */}
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-slate-600 dark:text-slate-300">Current balance</div>
              <div className="font-display text-4xl font-bold tabular text-slate-900 dark:text-slate-100 mt-1" data-testid="detail-balance">
                {fmtMoney(detailDebt?.balance || 0, cur)}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                Pay <span className="font-semibold text-slate-700 dark:text-slate-300 tabular">{fmtMoney(detailDebt?.minimum_payment || 0, cur)}</span>/mo
                · Monthly interest <span className="font-semibold text-rose-600 dark:text-rose-400 tabular">{fmtMoney(detailDebt?.monthly_interest || 0, cur)}</span>
              </div>
              {detailDebt?.next_due && (
                <div
                  data-testid="detail-due-chip"
                  className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
                    detailDebt.next_due.status === "paid"
                      ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                      : detailDebt.next_due.status === "urgent"
                      ? "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300"
                      : detailDebt.next_due.status === "soon"
                      ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <span>{detailDebt.next_due.status === "paid" ? "✅" : detailDebt.next_due.status === "urgent" ? "🚨" : detailDebt.next_due.status === "soon" ? "⏰" : "📅"}</span>
                  <span>
                    {detailDebt.next_due.status === "paid"
                      ? <>Paid this month — <span className="tabular">{fmtMoney(detailDebt.paid_this_month?.amount || 0, cur)}</span> of <span className="tabular">{fmtMoney(detailDebt.minimum_payment || 0, cur)}</span>{" · next due "}{new Date(detailDebt.next_due.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</>
                      : <>
                          {detailDebt.next_due.days_until === 0
                            ? "Due today"
                            : detailDebt.next_due.days_until === 1
                            ? "Due tomorrow"
                            : `Due in ${detailDebt.next_due.days_until} days`}
                          {" · "}
                          {new Date(detailDebt.next_due.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                          {" · pay "}
                          <span className="tabular">{fmtMoney(detailDebt.minimum_payment || 0, cur)}</span>
                        </>}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3" data-testid="detail-projections">
              {["3", "6", "12"].map(h => {
                const p = detailDebt?.projections?.[h];
                return (
                  <div key={h} className="rounded-2xl bg-slate-50 dark:bg-slate-800 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">In {h} months</div>
                    <div className="font-display text-lg font-bold tabular text-slate-900 dark:text-slate-100 mt-1">{fmtMoney(p?.final_balance || 0, cur)}</div>
                    <div className="text-[10px] text-slate-600 dark:text-slate-300">
                      <span className="tabular text-rose-600 dark:text-rose-400">+{fmtMoney(p?.total_interest || 0, cur)}</span> interest
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between text-xs rounded-2xl bg-slate-50 dark:bg-slate-800 p-3">
              <span className="text-slate-600 dark:text-slate-300 font-semibold">Time to payoff at current payment</span>
              <span className="tabular font-bold text-slate-900 dark:text-slate-100">
                {(() => {
                  const m = detailDebt?.projections?.["12"]?.months_to_payoff;
                  if (!m) return "Never at this rate";
                  return m >= 12 ? `${Math.floor(m / 12)}y ${m % 12}m` : `${m}m`;
                })()}
              </span>
            </div>

            {/* Log a payment form */}
            <div className="rounded-2xl border-2 border-dashed border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/5 p-4">
              <div className="font-display text-sm font-bold text-emerald-800 dark:text-emerald-300 mb-2 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Log a payment
              </div>
              <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mb-3">
                Creates a {detailDebt?.type === "credit_card" ? "Credit Cards" : "Loans"} expense in your transactions AND deducts it from this balance.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="rounded-full sm:col-span-1"
                  data-testid="pay-amount"
                />
                <Input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="rounded-full sm:col-span-1"
                  data-testid="pay-date"
                />
                <Input
                  placeholder="Note (optional)"
                  value={payDesc}
                  onChange={(e) => setPayDesc(e.target.value)}
                  className="rounded-full sm:col-span-2"
                  data-testid="pay-desc"
                />
              </div>
              <div className="mt-3 flex justify-end">
                <Button
                  onClick={logPayment}
                  disabled={paySaving || !payAmount}
                  className="rounded-full bg-spidey-red text-white hover:opacity-90"
                  data-testid="log-payment-btn"
                >
                  <Check className="w-4 h-4 mr-1" /> {paySaving ? "Logging…" : "Log payment"}
                </Button>
              </div>
            </div>

            {/* Payment history */}
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-slate-600 dark:text-slate-300 mb-2">
                Payment history ({payments.length})
              </div>
              {payments.length === 0 ? (
                <div className="text-xs text-slate-600 dark:text-slate-300 py-4 text-center rounded-2xl bg-slate-50 dark:bg-slate-800">
                  No payments applied yet.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                  {payments.map(p => (
                    <div key={p.id} data-testid={`payment-history-${p.id}`} className="flex justify-between items-center rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800 text-xs">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">{p.description}</div>
                        <div className="text-slate-600 dark:text-slate-300">{new Date(p.date).toLocaleDateString()}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="tabular font-bold text-rose-600 dark:text-rose-400">-{fmtMoney(p.amount, cur)}</div>
                        <button
                          type="button"
                          onClick={() => undoPayment(p)}
                          data-testid={`undo-payment-${p.id}`}
                          title="Undo this payment — deletes the transaction and adds the amount back to the balance"
                          className="w-6 h-6 rounded-full grid place-items-center text-slate-400 hover:bg-rose-100 hover:text-rose-600 dark:hover:bg-rose-500/20 dark:hover:text-rose-400 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="px-5 sm:px-6 py-4 border-t border-slate-200 dark:border-slate-800">
            <Button variant="outline" onClick={closeDetail} className="rounded-full" data-testid="detail-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-sync dialog */}
      <Dialog open={!!syncDebt} onOpenChange={(v) => !v && setSyncDebt(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="sync-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Auto-sync payments to {syncDebt?.name}
            </DialogTitle>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
              We found transactions that look like payments toward this debt. Selected ones will be subtracted from the balance and marked applied so they can't be counted twice.
            </p>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
            {(suggestions[syncDebt?.id]?.transactions || []).map((t) => {
              const isChecked = !!syncSelected[t.id];
              return (
                <label
                  key={t.id}
                  data-testid={`sync-row-${t.id}`}
                  className={`flex items-center gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${isChecked ? "border-spidey-red bg-red-50/70 dark:bg-red-500/10" : "border-slate-200 dark:border-slate-700"}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => setSyncSelected(s => ({ ...s, [t.id]: !s[t.id] }))}
                    data-testid={`sync-check-${t.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {t.description || "—"}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-1.5 flex-wrap">
                      <span>{new Date(t.date).toLocaleDateString()}</span>
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-widest ${t.confidence === "high" ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300"}`}
                        title={t.confidence === "high" ? "Description mentions this debt" : "Category matches but description is generic"}
                      >
                        {t.confidence} match
                      </span>
                    </div>
                  </div>
                  <div className="text-sm font-bold tabular text-rose-600 dark:text-rose-400 dark:text-rose-400 shrink-0">
                    -{fmtMoney(t.amount, cur)}
                  </div>
                </label>
              );
            })}
            {!(suggestions[syncDebt?.id]?.transactions?.length) && (
              <div className="text-sm text-slate-600 dark:text-slate-300 py-8 text-center">
                No matching payments right now.
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-between">
            <div className="text-xs text-slate-600 dark:text-slate-300 font-semibold">
              Will reduce balance by <span className="tabular text-rose-600 dark:text-rose-400">
                -{fmtMoney(
                  Object.keys(syncSelected).filter(k => syncSelected[k])
                    .reduce((s, k) => {
                      const t = (suggestions[syncDebt?.id]?.transactions || []).find(x => x.id === k);
                      return s + (t?.amount || 0);
                    }, 0),
                  cur
                )}
              </span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setSyncDebt(null)} className="rounded-full" data-testid="sync-cancel-btn">
                Cancel
              </Button>
              <Button
                variant="ghost"
                onClick={() => { const id = syncDebt?.id; setSyncDebt(null); if (id) dismissSyncFor(id); }}
                className="rounded-full text-slate-500 dark:text-slate-400 text-xs"
                data-testid="sync-dismiss-btn"
                title="Hide these suggestions for this debt from now on"
              >
                Skip — don't suggest
              </Button>
              <Button
                onClick={applySync}
                disabled={syncSaving || Object.values(syncSelected).every(v => !v)}
                className="rounded-full bg-spidey-red text-white hover:opacity-90"
                data-testid="sync-apply-btn"
              >
                <Check className="w-4 h-4 mr-1" /> {syncSaving ? "Applying…" : "Apply payments"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="debt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editingId ? "Edit debt" : "Add a new debt"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Type</label>
              <div className="flex gap-2 mt-1.5">
                {[
                  { k: "credit_card", label: "Credit Card", Icon: CreditCard },
                  { k: "loan", label: "Loan", Icon: Landmark },
                ].map(({ k, label, Icon }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, type: k }))}
                    data-testid={`debt-type-${k}`}
                    className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-full border text-sm font-semibold transition-colors ${form.type === k ? "border-spidey-red bg-spidey-red text-white" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Name</label>
              <Input placeholder="Chase Freedom / Sallie Mae Undergrad" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-full mt-1.5" data-testid="debt-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Balance</label>
                <Input type="number" step="0.01" placeholder="0.00" value={form.balance} onChange={(e) => setForm(f => ({ ...f, balance: e.target.value }))} className="rounded-full mt-1.5" data-testid="debt-balance" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">APR %</label>
                <Input type="number" step="0.01" placeholder="24.99" value={form.apr} onChange={(e) => setForm(f => ({ ...f, apr: e.target.value }))} className="rounded-full mt-1.5" data-testid="debt-apr" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Payment / mo</label>
                <Input type="number" step="0.01" placeholder="150.00" value={form.minimum_payment} onChange={(e) => setForm(f => ({ ...f, minimum_payment: e.target.value }))} className="rounded-full mt-1.5" data-testid="debt-payment" />
              </div>
              {form.type === "credit_card" && (
                <div>
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Credit limit (optional)</label>
                  <Input type="number" step="0.01" placeholder="8000" value={form.credit_limit} onChange={(e) => setForm(f => ({ ...f, credit_limit: e.target.value }))} className="rounded-full mt-1.5" data-testid="debt-credit-limit" />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">
                Due day of month <span className="normal-case font-medium">(optional — e.g. 15 for the 15th of each month)</span>
              </label>
              <Input
                type="number"
                min="1"
                max="31"
                step="1"
                placeholder="1 – 31"
                value={form.due_day_of_month}
                onChange={(e) => setForm(f => ({ ...f, due_day_of_month: e.target.value }))}
                className="rounded-full mt-1.5"
                data-testid="debt-due-day"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="debt-cancel">Cancel</Button>
            <Button onClick={save} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="debt-save">
              {editingId ? "Save" : "Add debt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function DebtCard({ d, cur, suggestion, onEdit, onDelete, onSync, onOpen, onDismissSync }) {
  const Icon = d.type === "credit_card" ? CreditCard : Landmark;
  const p6 = d.projections?.["6"] || {};
  const p12 = d.projections?.["12"] || {};
  const iconWrap = d.type === "credit_card"
    ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
    : "bg-stone-100 dark:bg-stone-800 text-stone-700";
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div
      data-testid={`debt-card-${d.id}`}
      onClick={() => onOpen(d)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(d)}
      className={`rounded-3xl border cursor-pointer hover:shadow-md dark:hover:shadow-black/40 hover:-translate-y-0.5 transition-all ${d.cant_cover_interest ? "border-rose-300 dark:border-rose-500/40" : "border-slate-200 dark:border-slate-800"} bg-white dark:bg-slate-900 p-5 sm:p-6`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${iconWrap}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{d.name}</div>
            <div className="text-xs text-slate-600 dark:text-slate-300 uppercase tracking-widest">
              {d.type === "credit_card" ? "Credit card" : "Loan"} · {d.apr}% APR
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={stop(() => onEdit(d))} data-testid={`edit-debt-${d.id}`} className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 grid place-items-center text-slate-600 dark:text-slate-300" title="Edit"><Pencil className="w-4 h-4" /></button>
          <button onClick={stop(() => onDelete(d))} data-testid={`delete-debt-${d.id}`} className="w-8 h-8 rounded-full hover:bg-rose-50 dark:hover:bg-rose-500/10 grid place-items-center text-rose-500 dark:text-rose-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="font-display text-3xl font-bold tabular text-slate-900 dark:text-slate-100 mb-1">{fmtMoney(d.balance, cur)}</div>
      <div className="text-xs text-slate-600 dark:text-slate-300 flex flex-wrap gap-x-4 gap-y-1">
        <span>Pay <span className="tabular font-semibold text-slate-700 dark:text-slate-300">{fmtMoney(d.minimum_payment, cur)}</span>/mo</span>
        <span>Monthly interest <span className="tabular font-semibold text-rose-600 dark:text-rose-400">{fmtMoney(d.monthly_interest, cur)}</span></span>
        {d.utilization != null && (
          <span>Utilization <span className={`tabular font-semibold ${d.utilization > 30 ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>{d.utilization}%</span></span>
        )}
      </div>

      {d.next_due && (
        <div
          data-testid={`due-chip-${d.id}`}
          className={`mt-3 flex items-center gap-2 rounded-2xl px-3 py-2 text-xs ${
            d.next_due.status === "paid"
              ? "bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
              : d.next_due.status === "urgent"
              ? "bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300"
              : d.next_due.status === "soon"
              ? "bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300"
              : "bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"
          }`}
        >
          <span className="text-sm">
            {d.next_due.status === "paid" ? "✅" : d.next_due.status === "urgent" ? "🚨" : d.next_due.status === "soon" ? "⏰" : "📅"}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">
              {d.next_due.status === "paid"
                ? `Paid this month — ${fmtMoney(d.paid_this_month?.amount || 0, cur)} of ${fmtMoney(d.minimum_payment, cur)}`
                : d.next_due.days_until === 0
                ? `Due today — pay ${fmtMoney(d.minimum_payment, cur)}`
                : d.next_due.days_until === 1
                ? `Due tomorrow — pay ${fmtMoney(d.minimum_payment, cur)}`
                : `Due in ${d.next_due.days_until} days — pay ${fmtMoney(d.minimum_payment, cur)}`}
            </div>
            <div className="text-[10px] opacity-80">
              {new Date(d.next_due.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              {" · "}
              {d.next_due.status === "paid" ? "next due date — you're all set" : "pay this to avoid a late fee"}
            </div>
          </div>
        </div>
      )}

      {d.utilization != null && (
        <div className="mt-3 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden" data-testid={`debt-util-bar-${d.id}`}>
          <div
            className={`h-full rounded-full ${d.utilization > 70 ? "bg-rose-500" : d.utilization > 30 ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${Math.min(100, d.utilization)}%` }}
          />
        </div>
      )}

      {suggestion && suggestion.count > 0 && (
        <div
          data-testid={`sync-chip-${d.id}`}
          className="mt-4 w-full flex items-center justify-between gap-2 rounded-2xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/70 dark:bg-emerald-500/10 px-4 py-2.5"
        >
          <button
            type="button"
            onClick={stop(() => onSync(d))}
            data-testid={`sync-chip-review-${d.id}`}
            className="flex-1 flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity text-left"
          >
            <Link2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs sm:text-sm font-bold text-emerald-800 dark:text-emerald-300 truncate">
                Auto-sync {suggestion.count} payment{suggestion.count > 1 ? "s" : ""} · -{fmtMoney(suggestion.total, cur)}
              </div>
              <div className="text-[10px] text-emerald-700/80 dark:text-emerald-400/80">
                Deduct matching transactions from this balance
              </div>
            </div>
          </button>
          <span className="text-emerald-700 dark:text-emerald-300 font-bold text-xs shrink-0 mr-1 pointer-events-none">Review</span>
          <button
            type="button"
            onClick={stop(() => onDismissSync?.())}
            data-testid={`sync-chip-dismiss-${d.id}`}
            aria-label="Hide auto-sync for this debt"
            title="Hide auto-sync for this debt"
            className="shrink-0 w-6 h-6 grid place-items-center rounded-full text-emerald-700/70 dark:text-emerald-300/70 hover:bg-emerald-200/60 dark:hover:bg-emerald-500/25 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {d.cant_cover_interest && (
        <div className="mt-3 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 p-3 flex items-start gap-2 text-xs">
          <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
          <div className="text-rose-700 dark:text-rose-300">
            <div className="font-bold">Payment can't cover interest</div>
            <div>Increase your monthly payment above {fmtMoney(d.monthly_interest, cur)} to start reducing the balance.</div>
          </div>
        </div>
      )}

      {/* Projections */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-slate-50 dark:bg-slate-800 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">In 6 months</div>
          <div className="font-display text-lg font-bold tabular text-slate-900 dark:text-slate-100 mt-1">{fmtMoney(p6.final_balance || 0, cur)}</div>
          <div className="text-[10px] text-slate-600 dark:text-slate-300">
            <span className="tabular text-rose-600 dark:text-rose-400">+{fmtMoney(p6.total_interest || 0, cur)}</span> interest
          </div>
        </div>
        <div className="rounded-2xl bg-spidey-red/10 dark:bg-spidey-red/20 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest spidey-red">In 12 months</div>
          <div className="font-display text-lg font-bold tabular text-slate-900 dark:text-slate-100 mt-1">{fmtMoney(p12.final_balance || 0, cur)}</div>
          <div className="text-[10px] text-slate-600 dark:text-slate-300">
            <span className="tabular text-rose-600 dark:text-rose-400">+{fmtMoney(p12.total_interest || 0, cur)}</span> interest
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-600 dark:text-slate-300">Time to payoff</span>
        <span className="tabular font-bold text-slate-900 dark:text-slate-100">
          {p12.months_to_payoff
            ? p12.months_to_payoff >= 12
              ? `${Math.floor(p12.months_to_payoff / 12)}y ${p12.months_to_payoff % 12}m`
              : `${p12.months_to_payoff}m`
            : "Never at this rate"}
        </span>
      </div>
    </div>
  );
}
